import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";

const FONT = "0.875rem";

const fmtAmt = (v) => (v == null ? "—" : `$${Math.round(v).toLocaleString()}`);
const fmtPct = (v, decimals = 1) =>
  v == null ? "—" : `${(v * 100).toFixed(decimals)}%`;

const headSx = {
  fontSize: "0.8rem",
  fontWeight: 600,
  py: 0.75,
  px: 1.5,
  whiteSpace: "nowrap",
  color: "text.secondary",
};
const cellSx = { fontSize: FONT, py: 0.5, px: 1.5, border: 0 };
const inputSx = {
  "& .MuiInputBase-input": {
    fontSize: FONT,
    textAlign: "right",
    py: "4px",
    px: 1,
  },
};

const TF = {
  variant: "filled",
  size: "small",
  fullWidth: true,
  InputLabelProps: { shrink: true },
};

const SummaryRow = ({ label, value, bold, indent, color }) => (
  <TableRow>
    <TableCell
      sx={{
        fontSize: FONT,
        fontWeight: bold ? 700 : 400,
        pl: indent ? 4 : 2,
        color: color ?? "inherit",
        border: 0,
        py: 0.5,
      }}
    >
      {label}
    </TableCell>
    <TableCell
      align={"right"}
      sx={{
        fontSize: FONT,
        fontWeight: bold ? 700 : 400,
        pr: 2,
        color: color ?? "inherit",
        border: 0,
        py: 0.5,
        minWidth: 140,
      }}
    >
      {value}
    </TableCell>
  </TableRow>
);

const SUBLINES = [
  { label: "GL Premises", key: "prem_ops" },
  { label: "GL Products", key: "pco" },
  { label: "Liquor", key: "liquor" },
  { label: "Auto", key: "auto" },
];

const XsRatingResults = ({ values, data, onUpdate }) => {
  if (!values) return null;

  const t = values.totals ?? {};
  const irpm = t.irpm ?? 0;

  // Manual adj and TRIA recompute live from the store so edits here take effect
  // immediately without re-running Calculate.
  const manualAdj = data?.manualAdjustment ?? t.manual_adjustment ?? 0;
  const triaPct = data?.triaPct ?? t.tria_pct ?? 0;

  const fsesTechnical = t.fses_technical ?? null;
  const minPremAdj =
    t.min_prem_adj ??
    (values.band_details ?? []).reduce((s, b) => s + (b.min_uplift ?? 0), 0);
  const fsesTechnicalInclMin =
    t.fses_technical_incl_min ?? (fsesTechnical ?? 0) + minPremAdj;
  const fsesGuideline = t.fses_guideline ?? null;
  const isoTechnical = t.iso_technical ?? null;
  const chargedPremium = (fsesGuideline ?? 0) + manualAdj;
  const triaPremium = chargedPremium * triaPct;
  const chargedWithTria = chargedPremium + triaPremium;
  const chargedToFsesTechnicalPct = fsesTechnicalInclMin
    ? chargedPremium / fsesTechnicalInclMin
    : null;
  const chargedToIsoTechnicalPct = isoTechnical
    ? chargedPremium / isoTechnical
    : null;

  return (
    <Stack spacing={1.5}>
      {/* Technical / Guideline breakdown — matches Excel XS Rating output rows */}
      <Table size={"small"}>
        <TableHead>
          <TableRow sx={{ backgroundColor: "grey.50" }}>
            <TableCell
              sx={{ ...headSx, color: "text.primary", fontWeight: 700 }}
            />
            <TableCell
              sx={{ ...headSx, color: "text.primary", fontWeight: 700 }}
              align={"right"}
            >
              {"Total"}
            </TableCell>
            {SUBLINES.map((sl) => (
              <TableCell key={sl.key} sx={headSx} align={"right"}>
                {sl.label}
              </TableCell>
            ))}
            <TableCell sx={headSx} align={"right"}>
              {"Min Prem Adj"}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
              {"FSES Technical Premium"}
            </TableCell>
            <TableCell sx={{ ...cellSx, fontWeight: 700 }} align={"right"}>
              {fmtAmt(fsesTechnicalInclMin)}
            </TableCell>
            {SUBLINES.map((sl) => (
              <TableCell
                key={sl.key}
                sx={{ ...cellSx, color: "text.secondary" }}
                align={"right"}
              >
                {fmtAmt(t[`fses_technical_${sl.key}`] ?? null)}
              </TableCell>
            ))}
            <TableCell
              sx={{
                ...cellSx,
                color: minPremAdj > 0 ? "warning.main" : "text.secondary",
              }}
              align={"right"}
            >
              {fmtAmt(minPremAdj)}
            </TableCell>
          </TableRow>

          <TableRow sx={{ backgroundColor: "grey.50" }}>
            <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <span>{"FSES Guideline Premium"}</span>
                {irpm !== 0 && (
                  <Typography
                    component={"span"}
                    fontSize={"0.75rem"}
                    color={irpm < 0 ? "success.main" : "warning.main"}
                  >
                    {`IRPM ${irpm >= 0 ? "+" : ""}${(irpm * 100).toFixed(1)}%`}
                  </Typography>
                )}
              </Stack>
            </TableCell>
            <TableCell sx={{ ...cellSx, fontWeight: 700 }} align={"right"}>
              {fmtAmt(fsesGuideline)}
            </TableCell>
            {SUBLINES.map((sl) => (
              <TableCell key={sl.key} sx={cellSx} align={"right"}>
                {fmtAmt(t[`fses_guideline_${sl.key}`] ?? null)}
              </TableCell>
            ))}
            <TableCell sx={cellSx} />
          </TableRow>

          {isoTechnical != null && (
            <TableRow>
              <TableCell
                sx={{ ...cellSx, fontWeight: 600, color: "text.secondary" }}
              >
                {"ISO Technical Benchmark"}
              </TableCell>
              <TableCell
                sx={{ ...cellSx, fontWeight: 700, color: "text.secondary" }}
                align={"right"}
              >
                {fmtAmt(isoTechnical)}
              </TableCell>
              {SUBLINES.map((sl) => (
                <TableCell
                  key={sl.key}
                  sx={{ ...cellSx, color: "text.secondary" }}
                  align={"right"}
                >
                  {fmtAmt(t[`iso_${sl.key}`] ?? null)}
                </TableCell>
              ))}
              <TableCell sx={cellSx} />
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Adjustments + final totals — editable manual adj and TRIA */}
      <Table size={"small"}>
        <TableBody>
          <SummaryRow
            label={"Manual Premium Adjustment ($)"}
            value={
              <PrettyNumberTextField
                {...TF}
                hiddenLabel
                value={data?.manualAdjustment ?? null}
                placeholder={"0"}
                onChangeNumber={(v) => onUpdate("manualAdjustment", v)}
                sx={inputSx}
              />
            }
          />
          <SummaryRow
            label={"Charged Premium"}
            value={fmtAmt(chargedPremium)}
            bold
          />
          <SummaryRow
            label={"TRIA (%)"}
            value={
              <PrettyNumberTextField
                {...TF}
                hiddenLabel
                value={data?.triaPct != null ? data.triaPct * 100 : null}
                placeholder={"0"}
                onChangeNumber={(v) =>
                  onUpdate("triaPct", v != null ? v / 100 : null)
                }
                sx={inputSx}
              />
            }
            indent
          />
          <SummaryRow
            label={`TRIA Premium (${fmtPct(triaPct)})`}
            value={fmtAmt(triaPremium)}
            indent
            color={"text.secondary"}
          />
          <SummaryRow
            label={"Charged incl. TRIA"}
            value={fmtAmt(chargedWithTria)}
            bold
          />
          {chargedToFsesTechnicalPct != null && (
            <SummaryRow
              label={"Charged / FSES Technical"}
              value={fmtPct(chargedToFsesTechnicalPct)}
              color={"text.secondary"}
            />
          )}
          {chargedToIsoTechnicalPct != null && (
            <SummaryRow
              label={"Charged / ISO Technical"}
              value={fmtPct(chargedToIsoTechnicalPct)}
              color={"text.secondary"}
            />
          )}
        </TableBody>
      </Table>
    </Stack>
  );
};

export default XsRatingResults;
